/**
 * Rest properties used to build the request for an object.
 */
export interface ObjectProperties {
    /** Object ID. */
    id?: number | string;
    /** Rest type. */
    type?: string;
    /** Rest URL. */
    url?: string;
}

/**
 * Raw object as returned by the REST API.
 *
 * Only `url` is read here; every subclass narrows the fields it needs, so the
 * rest stays unknown on purpose rather than being asserted up front.
 */
export interface ObjectRawData {
    url?: string;
    [key: string]: unknown;
}

/**
 * Base class to store Object data retrieved from REST API.
 * 
 * This will match a Drupal entity.
 */
class ObjectData {
    // Data type and Id.
    type: string;
    id: number | string;
    url?: string;

    // Rest properties used for request.
    // - id, Object ID
    // - type, Rest type
    // - url Rest URL
    properties: ObjectProperties = {};

    // Retrieved object data, raw object.
    object: Record<string, unknown> = {};

    // Raw data the object was built from.
    data?: ObjectRawData;

    /**
     * Generic constructor.
     * 
     * @param type
     *    The data / object type.
     */
    constructor(type: string, id: number | string, properties?: ObjectProperties, data?: ObjectRawData) {
      this.type = type;
      this.id = id;
      
      if (properties) {
        this.properties = properties;
      }
      
      // Set some basic data, for nodes and terms.
      if (data) {
        this.data = data;

        if (data.url) {
          this.url = data.url;
        }
      }      
    }
    
    /**
     * Get data type.
     */
    getType(): string {
      return this.type;
    }
        
    /**
     * Get Data Id.
     */
    getId(): number | string {
      return this.id;
    }

    /**
     * Get the remote URL for links.
     */
    getLinkUrl(): string | undefined {
      return this.url;
    }
    
    /**
     * Gets the Rest URL.
     */
    getRestUrl(): string | null {
      return this.properties && this.properties.url ? this.properties.url : null;
    }
      
}

export default ObjectData;
